import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, KeyVal, Page } from 'equasion-lib';
import ZonePickerModal, { useZoneScanOnLoad } from '@/lib/shared-coms/ZonePickerModal';
import ActionTopBar from '@/lib/shared-coms/ActionTopBar';
import { FiArrowDown, FiCheckCircle, FiEdit } from 'react-icons/fi';
import { formatDateOnly } from '@/lib/utils/dateUtils';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import { scanAlert } from '@/lib/scanning/scanAlert';
import './Move.scss';

// A quarantined unit moves between quarantine zones and stays quarantined; a stocked one moves
// between stock zones. The server refuses any other pairing.
const QUARANTINE_ZONE_TYPES = ['quarantine'];
const STOCK_ZONE_TYPES = ['stock'];

const Move = ({ serializedItemState, bulkInventoryEventCreate, selectedCompanyId }) => {
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();
  const [moved, setMoved] = useState(false);

  const {
    purchaseOrder,
    pedigree,
    serialNumber,
    gtin,
    status,
    zone: currentZone,
    itemBreakdown
  } = serializedItemState || {};
  const destinationZoneTypes = status === 'QUARANTINED' ? QUARANTINE_ZONE_TYPES : STOCK_ZONE_TYPES;
  // Quarantined in place on a stock shelf: the server refuses every Move of it (the processor would
  // land it STOCKED). Quarantine Cleanup is the tool that gathers it into a quarantine zone.
  const needsQuarantineCleanup = status === 'QUARANTINED' && currentZone?.zoneType === 'stock';

  const { clientPurchaseOrderNumber } = purchaseOrder || {};
  const currentZoneId = currentZone?.id;
  const currentZoneName = currentZone?.name || '';
  const { ITEM } = pedigree || {};

  const [destinationZoneId, setDestinationZoneId] = useState(null);
  const [destinationZoneName, setDestinationZoneName] = useState('');
  // The destination is the only thing this screen asks for, so its scanner opens with the screen.
  const { tapPrompt, pickerProps } = useZoneScanOnLoad(
    () => setModalOpen(true),
    !destinationZoneId && !needsQuarantineCleanup
  );
  const productTitle = (itemBreakdown || []).find(({ product }) => product?.title)
    ?.product?.title;

  const submitMove = async () => {
    try {
      setLoading(true);
      await bulkInventoryEventCreate({
        variables: {
          serialNumber,
          gtin,
          companyId: selectedCompanyId,
          inventoryEventAttributes: {
            type: 'MOVE',
            zoneId: destinationZoneId,
            companyId: selectedCompanyId
          }
        }
      });
      setMoved(true);
    } catch (e) {
      console.log(`ERROR MOVING ITEM: ${e}`);
      // A refusal (a unit in the case that cannot go there, say) must reach the operator in full.
      scanAlert('Not moved', operatorFacingMessage(e, 'The item could not be moved. Try again.'), 10);
    }
    setLoading(false);
  };

  return (
    <Page className="move action-page">
      <ActionTopBar action="Move" serialNumber={serialNumber} gtin={gtin} />
      <div className="action-page-body">
        <KeyVal
          items={[
            { key: 'PO Number', value: clientPurchaseOrderNumber },
            { key: 'Serial', value: serialNumber }
          ].filter(Boolean)}
        />
        <div className="current-zone">
          <h1>Current Zone</h1>
          <h2>{currentZoneName}</h2>
        </div>
        {needsQuarantineCleanup ? (
          <div className="quarantine-cleanup-notice">
            <p>
              This item is quarantined but sits in a stock zone, so it can't be moved from here.
              Use Quarantine Cleanup to move it into a quarantine zone.
            </p>
            <Button type="secondary" fullWidth onClick={() => navigate('/quarantine-cleanup')}>
              Open Quarantine Cleanup
            </Button>
          </div>
        ) : (
          <>
            <FiArrowDown />
            <div className="destination-zone" onClick={() => setModalOpen(true)}>
              <FiEdit />
              <h1>Destination Zone</h1>
              <h2 className={!destinationZoneName ? 'zone-placeholder' : ''}>
                {destinationZoneName || tapPrompt}
              </h2>
            </div>
          </>
        )}
        {/* <div className="header-row">
          <div className="row-header">ITEM</div>
          <div className="row-header">QTY</div>
          <div className="row-header">STATUS</div>
        </div>
        {(itemBreakdown || []).map(({ status, quantity, product, lot, expiration }) => (
          <Fragment key={lot}>
            <div className="data-row">
              <div className="row-data">
                {product?.title ?? 'SSCC'}
                <br />
                {lot} / {formatDateOnly(expiration) || expiration}
              </div>
              <div className="row-data">{quantity}</div>
              <div className="row-data">{status}</div>
            </div>
          </Fragment>
        ))} */}
        {!needsQuarantineCleanup && (
          <Button
            type="primary"
            fullWidth
            onClick={submitMove}
            disabled={loading || !destinationZoneId}
          >
            Move
          </Button>
        )}
      </div>
      <div className={['result-panel', moved && 'open'].filter(Boolean).join(' ')}>
        <div className="result-message">
          <FiCheckCircle style={{ color: 'var(--green)' }} />
          <h1>Moved Successfully</h1>
          <p>
            {ITEM} products from PO # {clientPurchaseOrderNumber} have been moved to zone{' '}
            <b>{destinationZoneName}</b>
          </p>
        </div>
        <div className="result-actions">
          <Button type="primary" fullWidth onClick={() => navigate('/')}>
            Done
          </Button>
        </div>
      </div>
      <ZonePickerModal
        className="move-zone-modal"
        open={modalOpen}
        {...pickerProps(() => setModalOpen(false))}
        title="Select a destination zone"
        label="Destination Zone"
        companyId={selectedCompanyId}
        zoneTypes={destinationZoneTypes}
        excludeZoneIds={currentZoneId ? [currentZoneId] : []}
        excludeMessage={`${currentZoneName || 'That zone'} is where the item already is — scan the zone it is moving to.`}
        scanTitle="Scan destination zone"
        scanHint={`Serial ${serialNumber}${productTitle ? ` · ${productTitle}` : ''}${
          currentZoneName ? `, now in ${currentZoneName}` : ''
        }. Scan the label on the zone it is moving into.`}
        onZoneChosen={({ id, display }) => {
          setDestinationZoneId(id);
          setDestinationZoneName(display);
        }}
      />
    </Page>
  );
};

export default Move;
